import { ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { DISPLAY_ROLES } from '../../config/areas';
import { logout } from '../../data/auth';
import { countryFlag, getMyRegionHint } from '../../data/me';
import type { UserInfo } from '../../data/types';
import { LanguageSwitcher } from './LanguageSwitcher';

interface UserMenuProps {
  user: UserInfo | null;
  roles: string[];
}

/** "Anika Sharma" -> "AS"; an email falls back to its first letter. */
function initialsOf(name: string): string {
  const words = name
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2);
  const letters = words.map((word) => word[0]).join('');
  return (letters || name[0] || '?').toUpperCase();
}

export function UserMenu({ user, roles }: UserMenuProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [menu, setMenu] = useState({ open: false, path: pathname });
  const [hint, setHint] = useState<{ countryCode: string | null; country: string | null; displayName: string | null }>({
    countryCode: null,
    country: null,
    displayName: null
  });

  useEffect(() => {
    void getMyRegionHint().then(setHint);
  }, []);

  // Same derived pattern as the area menu: the panel belongs to the route it was
  // opened on, so any navigation implicitly closes it without an effect.
  const open = menu.open && menu.path === pathname;

  const email = user?.userDetails;
  // SWA often omits the "name" claim for real AAD logins, so the claim-derived
  // name then equals the email — fall back to the Graph display name in that case.
  const claimName = user?.name;
  const name = (claimName && claimName !== email ? claimName : hint.displayName) ?? claimName ?? t('user.guest');
  const country = hint.country;
  const shown = roles.filter((role) => DISPLAY_ROLES.includes(role));

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setMenu((current) => ({ ...current, open: false }));
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  return (
    <div
      ref={containerRef}
      className="relative"
      onKeyDown={(event) => {
        if (event.key !== 'Escape' || !open) return;
        setMenu({ open: false, path: pathname });
        triggerRef.current?.focus();
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="user-menu"
        aria-label={t('user.menu', { name })}
        onClick={() => setMenu({ open: !open, path: pathname })}
        className="grid size-9 shrink-0 place-items-center rounded-full bg-accent text-xs font-semibold text-accent-foreground ring-1 ring-border transition hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        <span aria-hidden="true">{initialsOf(name)}</span>
      </button>

      {open && (
        <div
          id="user-menu"
          className="absolute right-0 top-full z-50 mt-2 w-64 rounded-lg border border-border bg-surface text-surface-foreground"
        >
          <div className="border-b border-border px-4 py-3">
            <p className="truncate text-sm font-semibold text-foreground">{name}</p>
            {email && email !== name && (
              <p className="truncate text-xs text-muted-foreground">{email}</p>
            )}
            {country && (
              <p className="truncate text-xs text-muted-foreground">
                {countryFlag(hint.countryCode)} {country}
              </p>
            )}
            {shown.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-1">
                {shown.map((role) => (
                  <li
                    key={role}
                    className="rounded bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
                  >
                    {t(`user.roles.${role}`)}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="border-b border-border px-2 py-2">
            <LanguageSwitcher variant="menu" />
          </div>

          <div className="p-2">
            <button
              type="button"
              onClick={logout}
              className="flex w-full items-center gap-2 rounded px-2 py-2 text-sm font-semibold text-foreground transition hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              <ArrowRightOnRectangleIcon className="size-4" aria-hidden="true" />
              {t('user.signOut')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
